'use client';
import styles from './navheader.module.scss';
import { NavDropdownContentProps, NavHeaderProps } from './NavHeader.type';
import { useCallback, useRef, useState, useEffect, Ref, HTMLAttributes } from 'react';
// TODO: dinamically import this once migrated into de-sxm-www
import { FocusTrap } from 'focus-trap-react';
import { NavLeftSection } from './NavHeaderComponents/NavLeftSection';
import { NavRightSection } from './NavHeaderComponents/NavRightSection';
import SearchBarContainer from './NavHeaderComponents/SearchBarContainer';
import { useScrollDirection } from '@/hooks/useScrollDirection';
import { useElementSize } from '@/hooks/useElementSize';
import { useCssVar } from '@/hooks/useCssVar';
import cn from 'classnames';
import useClickOutside from '@/hooks/useClickOutside';
import { useClickOutsideWithExceptions } from '@/hooks/useClickOutsideWithExceptions';
import { useHoverCloseTimeout } from '@/hooks/useHoverCloseTimeout';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { responsiveBreakpointsPx } from '@/constants';
import { isPopulatedNavDropdown } from './NavHeaderComponents/utils';
import { MobileDrawer } from './NavHeaderComponents/MobileDrawer';

export interface WidgetAttribute extends HTMLAttributes<HTMLElement> {
    class: string;
    ref?: Ref<HTMLElement>;
    'panel-margin-top': number;
    'icon-spacing'?: number;
    onClick: () => void;
}

interface UiAccountElement extends HTMLElement {
    closePanels: () => void;
}
declare module 'react' {
    // eslint-disable-next-line @typescript-eslint/no-namespace
    namespace JSX {
        interface IntrinsicElements extends HTMLElement {
            'sxm-ui-nav-account-presence-icons': WidgetAttribute;
        }
    }
}
/**
 * Publishes the announcement banner height into `--announcement-banner-height`.
 * Rendered only when NavHeader actually receives banners, so it never competes
 * with BannerHeightSync as a second writer of the same CSS variable.
 */
function BannerHeightCssVar({ height }: { height: number }): null {
    useCssVar('--announcement-banner-height', height);
    return null;
}

export function NavHeader({
    navLeftItems,
    helpCenterItem,
    trySiriusXMlink,
    appdownloadItem,
    theme = 'light',
    searchBar,
    search,
    account,
    onSearchClose,
    onSearchOpen,
    logo,
    logoUrl,
    avoidNavScroll = false,
    supportTitle,
    navRightCtas,
    mobileNavRightCtas,
    navTheme,
    searchOpen = false,
    announcementBanners,
    headerId,
    overlap = true,
    preventScrollUp = false,
    shouldLoadLegacyScripts = false,
}: NavHeaderProps): React.JSX.Element {
    const SEARCH_PAGE_NAME = 'overlay:search';
    // OneTrust can render the banner (#onetrust-banner-sdk) and preference center (#onetrust-pc-sdk)
    // as top-level elements without the #onetrust-consent-sdk wrapper, so match all three roots —
    // otherwise clicking the cookie banner is treated as an outside click and wrongly closes search.
    const MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR = '#onetrust-consent-sdk, #onetrust-banner-sdk, #onetrust-pc-sdk';
    const wrapperRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const navRef = useRef<HTMLElement>(null);
    const navAndBannersRef = useRef<HTMLDivElement | null>(null);
    const resizeObserverRef = useRef<ResizeObserver>(null);
    const [bannerRef, { height: bannerHeight }] = useElementSize<HTMLDivElement>();

    // scroll range b/c OSX has negative scroll and bounce, this prevents nav flicker
    const { scrollDir, onPageTop } = useScrollDirection(0, -100, 10);
    const [helpCenterHovered, setHelpCenterHovered] = useState<boolean>(false);
    const [navDropdownHoveredIndex, setNavDropdownHoveredIndex] = useState<number | null>(null);
    const [popoverHoveredIndex, setPopoverHoveredIndex] = useState<number | null>(null);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [activeInnerDrawerIndex, setActiveInnerDrawerIndex] = useState<number | null>(null);
    const accountWidget = useRef<UiAccountElement>(null);
    const [isDarkTheme, setIsDarkTheme] = useState(theme === 'dark');
    const [focusPause, setFocusPause] = useState(false);

    const { schedule: scheduleHoverClose, cancel: cancelHoverClose } = useHoverCloseTimeout(150, () => {
        setNavDropdownHoveredIndex(null);
        setPopoverHoveredIndex(null);
    });

    useClickOutside(accountWidget, {}, () => {
        accountWidget?.current?.closePanels?.();
    });

    useClickOutsideWithExceptions(
        wrapperRef,
        {},
        (state, event, exceptionMatch) => {
            if (!(event instanceof MouseEvent) || !event.target) return;
            const target = event.target as HTMLElement;
            const closest = exceptionMatch;
            setFocusPause(!!closest);
            if (!closest && searchOpen) {
                const searchResultsContainer = document.querySelector(`.${styles.searchContainer}`);
                if (searchResultsContainer) {
                    const { right } = searchResultsContainer.getBoundingClientRect();
                    if (event.clientX >= right - 15) {
                        return;
                    }
                }
                const isInsideSearchResults = target.closest(`.${styles.searchContainer}`);
                if (!isInsideSearchResults) {
                    onSearchClose?.();
                }
            }
        },
        MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR,
    );

    useClickOutsideWithExceptions(
        navAndBannersRef,
        {},
        (state, event, exceptionMatch) => {
            if (!(event instanceof MouseEvent) || !event.target) return;
            const target = event.target as HTMLElement;
            const closest = exceptionMatch;
            setFocusPause(!!closest);
            if (!closest && searchOpen) {
                const searchResultsContainer = document.querySelector(`.${styles.searchContainer}`);
                if (searchResultsContainer) {
                    const { right } = searchResultsContainer.getBoundingClientRect();
                    if (event.clientX >= right - 15) {
                        return;
                    }
                }
                const isInsideSearchResults = target.closest(`.${styles.searchContainer}`);
                if (!isInsideSearchResults) {
                    onSearchClose?.();
                }
            }
        },
        MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR,
    );

    // The search overlay and mobile drawer each cover the page below `nav-tablet`, so they
    // suppress page scrolling while open. Both are refcounted, so closing one no longer unlocks
    // the page while the other is still open.
    // Note: `- 1` because the CSS `max-width` range is inclusive, matching the `bp-max` mixin.
    const isMobileNavWidth = useMediaQuery(`(max-width: ${responsiveBreakpointsPx['nav-tablet'] - 1}px)`);

    useBodyScrollLock(searchOpen && isMobileNavWidth);
    useBodyScrollLock(drawerOpen);

    const toggleSearchBar = () => {
        if (searchOpen) {
            onSearchClose?.();
        } else {
            onSearchOpen?.();
        }

        setNavDropdownHoveredIndex(null);
        setPopoverHoveredIndex(null);
    };

    const handleSearchClose = useCallback(() => {
        onSearchClose?.();
    }, [onSearchClose]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (theme === 'dark' && scrollDir === 'up' && !onPageTop) {
                setIsDarkTheme(false);
            } else if (theme === 'dark' && onPageTop && navDropdownHoveredIndex === null) {
                setIsDarkTheme(true);
            }
            if (navDropdownHoveredIndex !== null) {
                handleSearchClose();
            }
        }, 16);

        return () => clearTimeout(timeoutId);
    }, [onPageTop, scrollDir, theme, navDropdownHoveredIndex, handleSearchClose]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleSearchClose();
            }
        };

        if (searchOpen) {
            document.addEventListener('keyup', handleEscape);
            const searchOpenEvent = new CustomEvent('modalopen', {
                bubbles: true,
                detail: { pagename: SEARCH_PAGE_NAME },
            });
            document.body.dispatchEvent(searchOpenEvent);
        } else {
            document.removeEventListener('keyup', handleEscape);
            const searchCloseEvent = new CustomEvent('modalclose', { bubbles: true });
            document.body.dispatchEvent(searchCloseEvent);
        }

        return () => {
            document.removeEventListener('keyup', handleEscape);
        };
    }, [searchOpen, handleSearchClose]);

    // NOT an overlay scroll lock — do not fold this into `useBodyScrollLock`. This is a
    // desktop-only workaround for a nav taller than the viewport: it early-returns on mobile (the
    // inverse of the overlay gate), and its `overflow: auto` write is a positive assertion that the
    // nav fits, not the release of a lock. A nav that fits must not be able to unlock a page an
    // open overlay is holding.
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        resizeObserverRef.current = new ResizeObserver(([entry]) => {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                const documentElement = document.documentElement;
                const isMobileNavWidth = documentElement.clientWidth < responsiveBreakpointsPx['nav-tablet'];

                if (isMobileNavWidth) {
                    // Mobile has different requirements so is handled by the `useBodyScrollLock`
                    // calls in this file
                    return;
                }
                const newNavHeight = entry?.borderBoxSize?.[0]?.blockSize;
                requestAnimationFrame(() => {
                    if (newNavHeight > documentElement.clientHeight) {
                        documentElement.style.overflow = 'hidden';
                    } else {
                        documentElement.style.overflow = 'auto';
                    }
                });
            }, 50);
        });

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, []);

    useEffect(() => {
        const scopedObserverRef = resizeObserverRef.current;

        if (scopedObserverRef && navRef.current) {
            requestAnimationFrame(() => {
                if (scopedObserverRef && navRef.current) {
                    scopedObserverRef.disconnect();
                    scopedObserverRef.observe(navRef.current);
                }
            });
        }
        return () => {
            scopedObserverRef?.disconnect();
        };
    }, [resizeObserverRef, navRef]);

    // We create the items for the mobile menu
    const activeInnerDrawer = (activeInnerDrawerIndex !== null && navLeftItems?.[activeInnerDrawerIndex]) || null;
    const navDropdownContentMobile = isPopulatedNavDropdown(activeInnerDrawer)
        ? {
              listItems:
                  activeInnerDrawer?.ctaSection?.buttonFields?.link_text &&
                  activeInnerDrawer?.ctaSection?.buttonFields?.href
                      ? [
                            {
                                linkText: activeInnerDrawer.ctaSection.buttonFields.link_text,
                                href: activeInnerDrawer.ctaSection.buttonFields.href,
                            },
                        ]
                      : [],
              tileContent: activeInnerDrawer?.tileSection,
          }
        : undefined;

    if (navDropdownContentMobile) {
        (activeInnerDrawer as NavDropdownContentProps)?.itemListSection?.forEach((item) =>
            navDropdownContentMobile.listItems.push({ linkText: item.title, href: item.href }),
        );
    }

    useEffect(() => {
        if (navDropdownHoveredIndex !== null || searchOpen) {
            accountWidget?.current?.closePanels?.();
        }
    }, [navDropdownHoveredIndex, searchOpen]);
    const isSimpleType = !navLeftItems?.length && !navRightCtas?.length;
    const navFixed = preventScrollUp
        ? false
        : avoidNavScroll ||
          (scrollDir === 'none'
              ? false
              : scrollDir === 'up'
                ? !onPageTop && !searchOpen
                : navDropdownHoveredIndex !== null);

    const navTopOffset = navFixed && announcementBanners && (onPageTop || searchOpen) ? bannerHeight : 0;

    return (
        <>
            <FocusTrap
                active={searchOpen}
                paused={focusPause}
                focusTrapOptions={{
                    allowOutsideClick: true,
                }}
            >
                <div
                    className={cn(styles.topWrapper, {
                        [styles.noOverlap]: !overlap,
                        [styles.searchOpenTopWrapper]: searchOpen,
                    })}
                    ref={wrapperRef}
                >
                    {announcementBanners && (
                        <>
                            <BannerHeightCssVar height={bannerHeight} />
                            <div
                                ref={bannerRef}
                                className={cn(styles.announcementBannerWrapper, {
                                    [styles.bannerFixed]: navFixed,
                                    [styles.bannerHidden]: !onPageTop && !searchOpen,
                                    [styles.bannerSearchHidden]: searchOpen,
                                })}
                            >
                                {announcementBanners}
                            </div>
                        </>
                    )}
                    <div ref={navAndBannersRef}>
                        <nav
                            ref={navRef}
                            style={navFixed ? { top: navTopOffset } : undefined}
                            className={cn(styles.wrapper, {
                                [styles.darktheme]: isDarkTheme,
                                [styles.navDropdownOpenStyles]: navDropdownHoveredIndex !== null || searchOpen,
                                [styles.searchOpenStyles]: searchOpen,
                                [styles.navFixed]: !searchOpen && navFixed,
                                [styles.headerBackground]:
                                    onPageTop && navDropdownHoveredIndex === null && !searchOpen && !avoidNavScroll,
                                [styles.whiteBackground]: avoidNavScroll,
                            })}
                            data-componenttype="Global Nav"
                            data-search-open={searchOpen ? 'true' : undefined}
                        >
                            <div className={styles.navStrip}>
                                <NavLeftSection
                                    handleSearchClose={handleSearchClose}
                                    isDarkTheme={isDarkTheme}
                                    navDropdownHoveredIndex={navDropdownHoveredIndex}
                                    setHelpCenterHovered={setHelpCenterHovered}
                                    setNavDropdownHoveredIndex={setNavDropdownHoveredIndex}
                                    searchOpen={searchOpen}
                                    scheduleHoverClose={scheduleHoverClose}
                                    cancelHoverClose={cancelHoverClose}
                                    headerId={headerId}
                                    logo={logo}
                                    logoUrl={logoUrl}
                                    navLeftItems={navLeftItems}
                                    popoverHoveredIndex={popoverHoveredIndex}
                                    setPopoverHoveredIndex={setPopoverHoveredIndex}
                                />
                                <NavRightSection
                                    search={search}
                                    searchOpen={searchOpen}
                                    toggleSearchBar={toggleSearchBar}
                                    handleSearchClose={handleSearchClose}
                                    buttonRef={buttonRef}
                                    isDarkTheme={isDarkTheme}
                                    account={account}
                                    accountWidget={accountWidget}
                                    appdownloadItem={appdownloadItem}
                                    helpCenterItem={helpCenterItem}
                                    supportTitle={supportTitle}
                                    helpCenterHovered={helpCenterHovered}
                                    setHelpCenterHovered={setHelpCenterHovered}
                                    navDropdownHoveredIndex={navDropdownHoveredIndex}
                                    setNavDropdownHoveredIndex={setNavDropdownHoveredIndex}
                                    trySiriusXMlink={trySiriusXMlink}
                                    navRightCtas={navRightCtas}
                                    navTheme={navTheme}
                                    onPageTop={onPageTop}
                                    isSimpleType={isSimpleType}
                                    setDrawerOpen={setDrawerOpen}
                                    shouldLoadLegacyScripts={shouldLoadLegacyScripts}
                                />
                            </div>
                            {search && (
                                <SearchBarContainer
                                    searchOpen={searchOpen}
                                    handleSearchClose={handleSearchClose}
                                    searchBar={searchBar}
                                    localpagename={SEARCH_PAGE_NAME}
                                />
                            )}
                        </nav>
                    </div>
                </div>
            </FocusTrap>
            <MobileDrawer
                isSimpleType={isSimpleType}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                navLeftItems={navLeftItems}
                supportTitle={supportTitle}
                setActiveInnerDrawerIndex={setActiveInnerDrawerIndex}
                activeInnerDrawerIndex={activeInnerDrawerIndex}
                navDropdownContentMobile={navDropdownContentMobile}
                helpCenterItem={helpCenterItem}
                trySiriusXMlink={trySiriusXMlink}
                navRightCtas={mobileNavRightCtas ?? navRightCtas}
                appdownloadItem={appdownloadItem}
            />
            <div
                className={cn(styles.modalBackground, {
                    [styles.showModal]: navDropdownHoveredIndex !== null || drawerOpen || searchOpen,
                    [styles.showModalSearch]: searchOpen,
                })}
            ></div>
        </>
    );
}
